import { PureComponent, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  Segment,
  Search,
  Icon,
  Popup,
  Pagination,
  Dimmer,
  Loader,
} from 'semantic-ui-react';

import { debounce, get, isEmpty } from '../../utils/lodash';
import { CustomSegment, CustomTable, EmptyContainer } from '../../components';

import {
  getBrowseActivities,
  getClosedProjectEventDetails,
} from '../../store/actions';

import { LibraryCard } from './LibraryCard';
import LibraryDetail from './LibraryDetail';

import './browse.scss';

const CARDS_PER_PAGE = 15;

const LS_MASTER_WIDTH_KEY = 'lib_master_width';
const DEFAULT_MASTER_WIDTH = 40;
const MIN_MASTER_WIDTH = 25;
const MAX_MASTER_WIDTH = 70;

function getStoredMasterWidth() {
  const saved = Number(localStorage.getItem(LS_MASTER_WIDTH_KEY));
  return saved >= MIN_MASTER_WIDTH && saved <= MAX_MASTER_WIDTH
    ? saved
    : DEFAULT_MASTER_WIDTH;
}

const SCOPES = (t) => [
  { value: 'mine', label: t('library.browseActivities.scopes.my_library') },
  {
    value: 'organisation',
    label: t('library.browseActivities.scopes.my_organisation'),
  },
  { value: 'platform', label: t('library.browseActivities.scopes.all_takso') },
];

class BrowseActivities extends PureComponent {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(get(props, 'match.search', ''));
    const activityParam = params.get('activity');
    this.splitRef = createRef();
    const { t } = this.props;

    this.state = {
      currentPage: 1,
      value: '',
      scope: 'platform',
      eventDetailFilter: [],
      selectedActivityId: activityParam ? Number(activityParam) : null,
      masterWidthPercent: getStoredMasterWidth(),
      isResizing: false,
      isPageLoading: true,
      hasResolvedSelection: false,
    };
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  handleResizeStart = (e) => {
    e.preventDefault();
    this.setState({ isResizing: true });
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizeMove = (e) => {
    const container = this.splitRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (!rect.width) return;
    const percent = ((e.clientX - rect.left) / rect.width) * 100;
    const clamped = Math.min(
      MAX_MASTER_WIDTH,
      Math.max(MIN_MASTER_WIDTH, percent)
    );
    this.setState({ masterWidthPercent: clamped });
  };

  handleResizeEnd = () => {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    this.setState({ isResizing: false }, () => {
      localStorage.setItem(
        LS_MASTER_WIDTH_KEY,
        Math.round(this.state.masterWidthPercent)
      );
    });
  };

  async componentDidMount() {
    try {
      const { currentPage, value, scope, eventDetailFilter } = this.state;
      this.props.getClosedProjectEventDetails(scope);
      await this.fetchActivities(currentPage, value, scope, eventDetailFilter, {
        preserveSelection: true,
      });
    } catch (error) {
      // console.error('BrowseActivities -> componentDidMount -> error', error);
    }
  }

  handleSearchChange = (e, { value }) => {
    this.setState({ value, currentPage: 1 }, () => {
      const { scope, eventDetailFilter } = this.state;
      this.fetchActivities(1, value, scope, eventDetailFilter);
    });
  };

  clearSearch = () => {
    this.setState({ value: '', currentPage: 1 }, () => {
      const { scope, eventDetailFilter } = this.state;
      this.fetchActivities(1, '', scope, eventDetailFilter);
    });
  };

  handleScopeChange = (scope) => {
    if (scope === this.state.scope) return;
    this.setState({ scope, currentPage: 1, eventDetailFilter: [] }, () => {
      const { value } = this.state;
      this.props.getClosedProjectEventDetails(scope);
      this.fetchActivities(1, value, scope, []);
    });
  };

  toggleEventDetailFilter = (id) => {
    const { eventDetailFilter } = this.state;
    const nextEventDetailFilter = eventDetailFilter.includes(id)
      ? eventDetailFilter.filter((typeId) => typeId !== id)
      : [...eventDetailFilter, id];
    this.setState(
      { eventDetailFilter: nextEventDetailFilter, currentPage: 1 },
      () => {
        const { value, scope } = this.state;
        this.fetchActivities(1, value, scope, nextEventDetailFilter);
      }
    );
  };

  clearEventDetailFilter = () => {
    this.setState({ eventDetailFilter: [], currentPage: 1 }, () => {
      const { value, scope } = this.state;
      this.fetchActivities(1, value, scope, []);
    });
  };

  pageChange = (page) => {
    const { value, scope, eventDetailFilter } = this.state;
    this.setState({ currentPage: page }, () => {
      this.fetchActivities(page, value, scope, eventDetailFilter);
    });
  };

  // Updates both the selection state and the URL (so the detail pane is
  // shareable/bookmarkable and survives a refresh)
  selectActivity = (id) => {
    this.setState({ selectedActivityId: id });
    const { navigate } = this.props;
    if (!navigate) return;
    navigate(id ? `/library?activity=${id}` : '/library', { replace: true });
  };

  // Returns whether it triggered a new detail-pane fetch, so the caller
  // knows whether to wait for handleDetailLoadingChange or clear the page
  // loader immediately (nothing left to wait for).
  applySelection = (docs, preserveSelection) => {
    const { selectedActivityId } = this.state;
    if (preserveSelection && selectedActivityId) return false;
    const nextId = !isEmpty(docs) ? docs[0].id : null;
    if (nextId !== selectedActivityId) {
      this.selectActivity(nextId);
      return true;
    }
    return false;
  };

  // Fired by LibraryDetail whenever ITS OWN fetch starts/stops, regardless
  // of whether that fetch was caused by a list reload or a plain card
  // click. Only the "finished" (false) signal is ever acted on here -
  // isPageLoading is only ever *set* true by fetchActivities itself, so a
  // stray true from a card click has nothing to do.
  handleDetailLoadingChange = (isLoading) => {
    if (!isLoading) {
      this.setState({ isPageLoading: false });
    }
  };

  fetchActivities = async (
    page,
    value,
    scope,
    eventDetailFilter,
    { preserveSelection = false } = {}
  ) => {
    this.setState({ isPageLoading: true });
    const result = await this.props.getBrowseActivities(
      'activityBrowse',
      page,
      value,
      CARDS_PER_PAGE,
      scope,
      eventDetailFilter
    );
    const pages = get(result, 'pages', 0);
    let finalResult = result;
    if (pages > 0 && page > pages) {
      this.setState({ currentPage: pages });
      finalResult = await this.props.getBrowseActivities(
        'activityBrowse',
        pages,
        value,
        undefined,
        scope,
        eventDetailFilter
      );
    }
    const wasAlreadyResolved = this.state.hasResolvedSelection;
    const selectionChanged = this.applySelection(
      get(finalResult, 'docs', []),
      preserveSelection
    );
    if (!wasAlreadyResolved) {
      // LibraryDetail mounts for the first time right now, with whatever
      // activityId applySelection landed on (including null, for a
      // genuinely empty list) - its own onLoadingChange callback will
      // clear isPageLoading once that resolves.
      this.setState({ hasResolvedSelection: true });
    } else if (!selectionChanged) {
      // LibraryDetail is already mounted and its activityId prop isn't
      // changing, so componentDidUpdate won't fire a new fetch - nothing
      // left to wait for.
      this.setState({ isPageLoading: false });
    }
  };

  render() {
    const {
      value,
      currentPage,
      scope,
      eventDetailFilter,
      selectedActivityId,
      masterWidthPercent,
      isResizing,
      isPageLoading,
      hasResolvedSelection,
    } = this.state;

    const { t, allActivities, closedProjectEventDetails } = this.props;
    const totalFilterCount = eventDetailFilter.length;
    const docs = allActivities.docs || [];

    return (
      <CustomSegment
        title={t('library.browseActivities.activityBrowser')}
        subTitle={t('library.browseActivities.activityBrowserSubTitle')}
        rightHeader={
          <div className="library-act">
            <Search
              className="activitySearch"
              onSearchChange={debounce(this.handleSearchChange, 1000, {
                leading: true,
              })}
              value={value}
              placeholder={t('library.browseActivities.searchActivities')}
              icon={
                <Icon
                  name={value ? 'close' : 'search'}
                  link={!!value}
                  onClick={value ? this.clearSearch : undefined}
                />
              }
            />
            <div className="lib-scope">
              {SCOPES(t).map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={`lib-scope-btn${
                    scope === s.value ? ' lib-scope-btn--active' : ''
                  }`}
                  onClick={() => this.handleScopeChange(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <Popup
              on="click"
              position="bottom right"
              className="library-filter-popup"
              trigger={
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  className="lib-filter-btn"
                  title={t('activity.browse.filter_by_activity_type')}
                >
                  <Icon name="sliders horizontal" />
                  {totalFilterCount > 0 && (
                    <span className="lib-filter-badge">{totalFilterCount}</span>
                  )}
                </button>
              }
            >
              <div className="lib-filter-panel">
                <div className="lib-filter-section">
                  <div className="lib-filter-section-header">
                    <span className="lib-filter-label">
                      {t('common.activity_type')}
                    </span>
                    <span className="lib-filter-clear-wrap">
                      {!isEmpty(eventDetailFilter) && (
                        <button
                          type="button"
                          className="lib-filter-link-btn"
                          onClick={this.clearEventDetailFilter}
                        >
                          {t('actions.clear').toLowerCase()}
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="lib-filter-checks">
                    {isEmpty(closedProjectEventDetails) ? (
                      <span className="lib-filter-empty">
                        {t('activity.browse.no_event_details_recorded_yet')}
                      </span>
                    ) : (
                      closedProjectEventDetails.map((detail) => (
                        <label key={detail.id} className="lib-filter-check">
                          <input
                            type="checkbox"
                            checked={eventDetailFilter.includes(detail.id)}
                            onChange={() =>
                              this.toggleEventDetailFilter(detail.id)
                            }
                          />
                          {detail.detail}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Popup>
          </div>
        }
      >
        <div
          className={`lib-split${isResizing ? ' lib-split--resizing' : ''}`}
          ref={this.splitRef}
          style={{ '--lib-master-width': `${masterWidthPercent}%` }}
        >
          {isPageLoading && (
            <Dimmer active inverted className="lib-split-loading">
              <Loader active size="large">
                {t('common.loading')}…
              </Loader>
            </Dimmer>
          )}
          <Segment basic className="lib-master">
            {docs.length ? (
              <>
                <div className="lib-master__list">
                  {docs.map((activity) => (
                    <LibraryCard
                      key={activity.id}
                      activity={activity}
                      selectedActivityId={selectedActivityId}
                      onSelect={this.selectActivity}
                    />
                  ))}
                </div>
                {allActivities.pages > 1 && (
                  <div className="lib-master__pagination">
                    <Pagination
                      size="tiny"
                      activePage={currentPage}
                      onPageChange={(e, { activePage }) =>
                        this.pageChange(activePage)
                      }
                      totalPages={allActivities.pages}
                      pointing
                      secondary
                      firstItem={null}
                      lastItem={null}
                      siblingRange={1}
                    />
                  </div>
                )}
              </>
            ) : null}
          </Segment>
          <div
            className="lib-resize-handle"
            onMouseDown={this.handleResizeStart}
          />
          <div className="lib-detail">
            {hasResolvedSelection && (
              <LibraryDetail
                activityId={selectedActivityId}
                scope={scope}
                suppressOwnLoader={isPageLoading}
                onLoadingChange={this.handleDetailLoadingChange}
              />
            )}
          </div>
        </div>
      </CustomSegment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    ...state.activity,
    allActivities: state.activity.allActivities,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getBrowseActivities,
      getClosedProjectEventDetails,
    },
    dispatch
  );
};

BrowseActivities = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(BrowseActivities));

export default BrowseActivities;
